/**
 * DXF HEADER persistence for AutoCAD document-level *point* system variables —
 * currently `$INSBASE` (the drawing insertion base point, AutoCAD BASE command
 * target).
 *
 * AutoCAD writes a point HEADER var as a `9 $NAME` line followed by three
 * value groups carrying X / Y / Z on consecutive codes:
 *
 *   9
 *   $INSBASE
 *   10
 *   <x>
 *   20
 *   <y>
 *   30
 *   <z>
 *
 * Group codes 10 / 20 / 30 are the Autodesk-DXF-reference + ezdxf convention
 * for HEADER point variables ($INSBASE is an ezdxf Vec3 written with code 10).
 * They are DISTINCT from the per-layout AcDbLayout insertion base (group codes
 * 12 / 22 / 32) handled in the LAYOUT object writer — do not conflate them.
 *
 * This module is the point-variable sibling of `headerVarCodec.ts` (numeric,
 * single-code-per-var) and `headerDocumentVars.ts`
 * ($INSUNITS / $PDMODE / $PDSIZE). It owns the typed object + read / write /
 * patch API for the point-var family, parameterised by a {@link HeaderPointVarSpec}
 * table so additional point vars ($EXTMIN / $EXTMAX / $LIMMIN / $LIMMAX) can be
 * folded in later without a structural rewrite.
 *
 * AutoCAD reference: drawing system-variable reference 2018+; DXF HEADER
 * section variable group codes.
 */

import type { DxfGroup, DxfPoint3D } from "./types";

// ── Public types ─────────────────────────────────────────────────────────

/**
 * Decoded values for the document-level point HEADER variables. Every field is
 * optional so a legacy DXF without the var yields an empty object — the bridge
 * layer applies the schema default (`insertionBase: { x: 0, y: 0, z: 0 }`).
 */
export type HeaderPointVars = {
  /** $INSBASE — group codes 10/20/30 — drawing insertion base point. */
  insertionBase?: DxfPoint3D;
};

/**
 * Descriptor for one point HEADER variable. `codes` is the [X, Y, Z] group-code
 * triple; the Z value group is OPTIONAL on read (legacy 2D headers may omit
 * code 30 — Z defaults to 0) but always emitted on write for AutoCAD parity.
 */
type HeaderPointVarSpec = {
  /** Full `$NAME` token as written on the group-9 line. */
  name: string;
  /** Field key on {@link HeaderPointVars}. */
  field: keyof HeaderPointVars;
  /** [X, Y, Z] DXF group codes (e.g. [10, 20, 30] for $INSBASE). */
  codes: readonly [number, number, number];
  /**
   * The AutoCAD default. An ABSENT var (one not already present in the HEADER
   * groups) is only emitted when it differs from this default, so a default
   * value is never invented into a header that did not carry the var. A var
   * that ALREADY EXISTS in the header is always overwritten in-place
   * regardless, so an edit back to the default still persists.
   */
  defaultValue: DxfPoint3D;
};

const VAR_FIELDS: ReadonlyArray<HeaderPointVarSpec> = [
  {
    name: "$INSBASE",
    field: "insertionBase",
    codes: [10, 20, 30],
    defaultValue: { x: 0, y: 0, z: 0 },
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function pointsEqual(a: DxfPoint3D, b: DxfPoint3D): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

/** Emit the `9 $NAME` + X/Y/Z value groups for one point var. */
function emitPointVar(spec: HeaderPointVarSpec, value: DxfPoint3D): DxfGroup[] {
  const [cx, cy, cz] = spec.codes;
  return [
    { code: 9, value: spec.name },
    { code: cx, value: String(value.x) },
    { code: cy, value: String(value.y) },
    { code: cz, value: String(value.z) },
  ];
}

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Scan a HEADER section's flat group list and extract the point sysvars.
 * Recognises the `9 $NAME` + X/Y/Z (codes 10/20/30) layout. The Z group is
 * optional — when absent Z defaults to 0. Non-finite values abort that var
 * (left absent → schema default applies). Unknown variables are ignored.
 */
export function readPointVarsFromHeader(
  headerGroups: ReadonlyArray<DxfGroup>,
): HeaderPointVars {
  const result: HeaderPointVars = {};
  const byName = new Map(VAR_FIELDS.map((s) => [s.name, s]));

  for (let i = 0; i < headerGroups.length - 1; i++) {
    const nameGroup = headerGroups[i];
    if (!nameGroup || nameGroup.code !== 9) continue;
    const spec = byName.get(nameGroup.value.trim());
    if (!spec) continue;

    const [cx, cy, cz] = spec.codes;
    let x: number | undefined;
    let y: number | undefined;
    let z = 0;
    let consumed = 0;
    // Walk the value groups immediately following the $NAME line. Stop at the
    // next group-9 (the following var) or end of section.
    for (let j = i + 1; j < headerGroups.length; j++) {
      const g = headerGroups[j];
      if (!g || g.code === 9) break;
      if (g.code === cx) x = Number.parseFloat(g.value);
      else if (g.code === cy) y = Number.parseFloat(g.value);
      else if (g.code === cz) z = Number.parseFloat(g.value);
      else break; // unexpected code inside the var body → stop defensively
      consumed++;
    }
    if (
      x !== undefined &&
      y !== undefined &&
      Number.isFinite(x) &&
      Number.isFinite(y) &&
      Number.isFinite(z)
    ) {
      result[spec.field] = { x, y, z };
    }
    i += consumed; // skip the matched value groups
  }

  return result;
}

// ── Write ────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs for the point HEADER variables. Symmetric with
 * {@link readPointVarsFromHeader}. Feeds the minimal-HEADER fallback (no
 * existing HEADER section), so a var equal to its `defaultValue` is skipped —
 * an absent source var is never invented into a freshly synthesised HEADER.
 */
export function writePointVarsToHeaderLines(
  vars: HeaderPointVars,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  for (const spec of VAR_FIELDS) {
    const value = vars[spec.field];
    if (value === undefined) continue;
    if (pointsEqual(value, spec.defaultValue)) continue;
    out.push(...emitPointVar(spec, value));
  }
  return out;
}

// ── Patch ──────────────────────────────────────────────────────────────────

/**
 * Splice point HEADER variable entries into an existing HEADER section group
 * array:
 *
 * 1. Walk the existing groups. When a `9 $NAME` entry matches a var present in
 *    `vars`, replace the following X/Y/Z value groups (the contiguous run of
 *    the var's three codes) with the new values — an UNCONDITIONAL in-place
 *    overwrite (an edit back to the default still persists).
 * 2. Append any vars present in `vars` but ABSENT from the existing groups —
 *    only when the value differs from the spec's `defaultValue`, so an
 *    absent-in-source default is never invented.
 *
 * Every other HEADER variable is preserved verbatim and the section is not
 * reordered, keeping the file as close to the original AutoCAD output as
 * possible.
 */
export function patchPointVarsIntoHeaderGroups(
  existingGroups: ReadonlyArray<DxfGroup>,
  vars: HeaderPointVars,
): DxfGroup[] {
  if (Object.keys(vars).length === 0) return existingGroups.slice();

  const byName = new Map(VAR_FIELDS.map((s) => [s.name, s]));
  const handled = new Set<keyof HeaderPointVars>();
  const patched: DxfGroup[] = [];

  for (let i = 0; i < existingGroups.length; i++) {
    const group = existingGroups[i]!;
    if (group.code === 9) {
      const spec = byName.get(group.value.trim());
      const value = spec ? vars[spec.field] : undefined;
      if (spec && value !== undefined) {
        const codeSet = new Set<number>(spec.codes);
        patched.push(group); // keep the $NAME group as-is
        // Skip the original contiguous run of this var's value groups.
        let j = i + 1;
        for (; j < existingGroups.length; j++) {
          const g = existingGroups[j];
          if (!g || g.code === 9 || !codeSet.has(g.code)) break;
        }
        patched.push(...emitPointVar(spec, value).slice(1)); // value groups only
        handled.add(spec.field);
        i = j - 1; // advance past the consumed value groups
        continue;
      }
    }
    patched.push(group);
  }

  // Append any vars not found in the original section — only when non-default.
  for (const spec of VAR_FIELDS) {
    if (handled.has(spec.field)) continue;
    const value = vars[spec.field];
    if (value === undefined) continue;
    if (pointsEqual(value, spec.defaultValue)) continue;
    patched.push(...emitPointVar(spec, value));
  }

  return patched;
}
